/**
 * `DocTree` — the demo's left-hand file tree (demo lines 2411–2417).
 *
 * A filter box over a two-level accordion: folders, optionally sub-grouped,
 * with one button per file. A file button carries its format as an uppercase
 * chip, exactly as the demo's `.tag-fmt` did.
 *
 * Open state is the component's, seeded from each folder's `defaultOpen` and
 * re-seeded whenever the folder set changes — which is what makes a filtered
 * tree open every folder that still has a hit, as the demo's filter did.
 *
 * UI layer. Every derivation is in `DataBrowserScene.transform.ts`.
 *
 * @see docs/logic/integration/data.md
 */

import { useEffect, useMemo, useState } from 'react';
import { ChevronRight, Search } from 'lucide-react';
import { cn } from '@/shared/cn';
import type { TreeFile, TreeFolder } from './DataBrowserScene.transform';

export interface DocTreeProps {
  folders: readonly TreeFolder[];
  /** Total files across the unfiltered tree, for the count line. */
  totalFiles: number;
  connectorCount: number;
  query: string;
  onQueryChange: (value: string) => void;
  /** The open document's key, so the button can read as selected. */
  selectedKey: string | null;
  onSelect: (key: string) => void;
  /** `true` while the tree's own data is still loading. */
  loading: boolean;
}

export function DocTree({
  folders,
  totalFiles,
  connectorCount,
  query,
  onQueryChange,
  selectedKey,
  onSelect,
  loading,
}: DocTreeProps) {
  const signature = useMemo(
    () => folders.map((f) => `${f.id}:${f.defaultOpen ? 1 : 0}`).join('|'),
    [folders],
  );
  const [open, setOpen] = useState<Record<string, boolean>>({});

  // Re-seed whenever the folder set or its default open state changes: a
  // filter that narrows the tree should expand what is left.
  useEffect(() => {
    const next: Record<string, boolean> = {};
    for (const folder of folders) {
      next[folder.id] = folder.defaultOpen;
      // The demo opens a group of twelve files or fewer.
      for (const group of folder.groups) next[group.id] = group.files.length <= 12;
    }
    setOpen(next);
    // `signature` is the stable identity of `folders` for this purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  const toggle = (id: string) => setOpen((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className="flex min-h-0 flex-col">
      <p
        className="mono px-1 pb-2 text-[10.5px] uppercase tracking-[0.08em]"
        style={{ color: 'var(--ink-tertiary)' }}
      >
        {loading
          ? 'Loading the store…'
          : `${totalFiles.toLocaleString('en-AU')} items · ${connectorCount} connectors · 14 native formats`}
      </p>

      <div className="relative px-1 pb-2">
        <Search
          className="pointer-events-none absolute left-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2"
          style={{ color: 'var(--ink-tertiary)' }}
          aria-hidden="true"
        />
        <input
          type="search"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="Filter files, machines, systems…"
          aria-label="Filter files, machines and systems"
          autoComplete="off"
          className="w-full rounded-lg border py-1.5 pl-8 pr-2 text-[12px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
          style={{
            borderColor: 'var(--hairline)',
            background: 'var(--surface-1)',
            color: 'var(--ink-primary)',
          }}
        />
      </div>

      <div className="scroll-soft min-h-0 flex-1 overflow-y-auto pr-1">
        {folders.length === 0 && !loading ? (
          <p className="px-2 py-3 text-[12px]" style={{ color: 'var(--ink-tertiary)' }}>
            Nothing matches that filter.
          </p>
        ) : null}

        {folders.map((folder) => (
          <div key={folder.id} className="mb-0.5">
            <FolderButton
              label={folder.name}
              count={folder.count}
              open={Boolean(open[folder.id])}
              onClick={() => toggle(folder.id)}
            />
            {open[folder.id] ? (
              <div className="pl-2">
                {folder.files.map((file) => (
                  <FileButton
                    key={file.key}
                    file={file}
                    selected={file.key === selectedKey}
                    onSelect={onSelect}
                  />
                ))}
                {folder.groups.map((group) => (
                  <div key={group.id}>
                    <FolderButton
                      label={group.name}
                      count={group.files.length}
                      open={Boolean(open[group.id])}
                      onClick={() => toggle(group.id)}
                      nested
                    />
                    {open[group.id] ? (
                      <div className="pl-2">
                        {group.files.map((file) => (
                          <FileButton
                            key={file.key}
                            file={file}
                            selected={file.key === selectedKey}
                            onSelect={onSelect}
                          />
                        ))}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

function FolderButton({
  label,
  count,
  open,
  onClick,
  nested,
}: {
  label: string;
  count: number;
  open: boolean;
  onClick: () => void;
  nested?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={open}
      className={cn(
        'flex w-full items-center gap-1.5 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-[var(--surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]',
        nested ? 'text-[11.5px]' : 'text-[12px] font-medium',
      )}
      style={{ color: nested ? 'var(--ink-secondary)' : 'var(--ink-primary)' }}
    >
      <ChevronRight
        className={cn('h-3 w-3 shrink-0 transition-transform', open ? 'rotate-90' : '')}
        aria-hidden="true"
      />
      <span className="min-w-0 flex-1 truncate">{label}</span>
      <span className="mono shrink-0 text-[10.5px]" style={{ color: 'var(--ink-tertiary)' }}>
        {count}
      </span>
    </button>
  );
}

function FileButton({
  file,
  selected,
  onSelect,
}: {
  file: TreeFile;
  selected: boolean;
  onSelect: (key: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(file.key)}
      aria-current={selected ? 'true' : undefined}
      title={file.machines.length ? `${file.label} · ${file.machines.join(' ')}` : file.label}
      className="flex w-full items-center gap-1.5 rounded-lg px-2 py-1 text-left transition-colors hover:bg-[var(--surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
      style={
        selected
          ? { background: 'var(--accent-soft)', color: 'var(--accent)' }
          : { color: 'var(--ink-secondary)' }
      }
    >
      <span
        className="mono shrink-0 rounded px-1 py-px text-[9px] font-semibold uppercase tracking-wide"
        style={{ background: 'var(--surface-3)', color: 'var(--ink-tertiary)' }}
      >
        {file.ext}
      </span>
      <span className="min-w-0 flex-1 truncate text-[11.5px]">{file.label}</span>
    </button>
  );
}
